import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { contentPreferenceRevision } from '../utils/contentPreferences';
import { Button, LoadingIndicator } from './ui/Primitives';

// Reuse recent progress in this tab without persisting call content in browser storage.
const recentTranslations = new Map();
let cacheScope;
function translationCache(token, revision) {
  const scope = JSON.stringify([token, revision]);
  if (scope !== cacheScope) { recentTranslations.clear(); cacheScope = scope; }
  for (const [key, value] of recentTranslations) {
    if (value.expiresAt <= Date.now()) recentTranslations.delete(key);
  }
  return recentTranslations;
}

export default function TranscriptLanguageView({ call, disabled, children }) {
  const [view, setView] = useState('original');
  const target = call.translationLanguage;
  const language = view === 'translated' && target ? target.code : 'original';
  const [result, setResult] = useState(null);
  const [retry, setRetry] = useState(0);
  const progressRef = useRef(null);
  const source = JSON.stringify(call.transcript || []);
  const visibility = call.contentRedaction?.visibility;
  const contentMode = visibility === 'original' ? 'original' : 'redacted';
  const requestKey = JSON.stringify([call.id, source, contentMode, language]);

  useEffect(() => {
    if (language === 'original' || disabled) return undefined;
    const controller = new AbortController();
    let active = true;
    const token = localStorage.getItem('jwt_authorization');
    const revision = contentPreferenceRevision();
    const current = () => active && token === localStorage.getItem('jwt_authorization') && revision === contentPreferenceRevision();
    const cache = translationCache(token, revision);
    if (progressRef.current?.key !== requestKey || progressRef.current?.token !== token || progressRef.current?.revision !== revision) {
      progressRef.current = cache.get(requestKey)?.progress || { key: requestKey, token, revision, transcript: [], nextOffset: 0, sourceVersion: undefined };
    }
    let progress = progressRef.current;
    setResult({ key: requestKey, transcript: progress.transcript, complete: progress.nextOffset === null });

    async function translateRemaining() {
      try {
        while (current() && progress.nextOffset !== null) {
          const offset = progress.nextOffset;
          const response = await axios.post(`${process.env.REACT_APP_BACKEND_HOST}/v2/calls/${call.id}/transcript-translation`,
            { language, contentMode, offset, sourceVersion: progress.sourceVersion },
            { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal, timeout: 60000 },
          );
          if (!current()) return;
          const data = response.data;
          if (data.language !== language || JSON.stringify(data.sourceTranscript) !== source || (progress.sourceVersion && data.sourceVersion !== progress.sourceVersion)) {
            progressRef.current = null;
            cache.delete(requestKey);
            setResult({ key: requestKey, error: 'The transcript changed. Reload the call to translate the latest version.' });
            return;
          }
          const total = data.sourceTranscript.length;
          const count = Array.isArray(data.transcript) ? data.transcript.length : 0;
          const end = offset + count;
          if (!count || data.offset !== offset || data.total !== total || typeof data.sourceVersion !== 'string' || end > total || data.nextOffset !== (end === total ? null : end)) {
            throw new Error('Invalid translation batch');
          }
          progress = { ...progress, transcript: [...progress.transcript, ...data.transcript], nextOffset: data.nextOffset, sourceVersion: data.sourceVersion };
          progressRef.current = progress;
          cache.delete(requestKey);
          cache.set(requestKey, { progress, expiresAt: Date.now() + 5 * 60 * 1000 });
          while (cache.size > 10) cache.delete(cache.keys().next().value);
          setResult({ key: requestKey, transcript: progress.transcript, complete: progress.nextOffset === null });
        }
      } catch (error) {
        if (!current()) return;
        const invalidated = [401, 403, 404, 409].includes(error.response?.status);
        if (invalidated) { progressRef.current = null; cache.delete(requestKey); }
        setResult({ key: requestKey, transcript: invalidated ? [] : progress.transcript,
          error: error.response?.data?.error?.message || 'The remaining translation could not be completed. Try again to continue, or view the original language.' });
      }
    }
    translateRemaining();
    return () => { active = false; controller.abort(); };
  }, [call.id, contentMode, disabled, language, requestKey, retry, source]);

  const currentResult = result?.key === requestKey ? result : null;
  const showingTranslation = language !== 'original' && !disabled;
  const translated = showingTranslation && currentResult?.transcript?.length > 0;
  const languageName = target?.name;

  return <>
    <div className="call-transcript-language">
      <label htmlFor="transcript-view-language">View in</label>
      <select id="transcript-view-language" className="say-select" value={language === 'original' ? 'original' : 'translated'} disabled={disabled || !target}
        onChange={event => { setResult(null); setView(event.target.value); }}>
        <option value="original">Original language</option>
        {target ? <option value="translated">{target.name}</option> : null}
      </select>
    </div>
    {translated ? <div className="call-transcript-translation-status"><p>AI translation into {languageName}. The original transcript and recording are unchanged.</p></div> : null}
    {!showingTranslation ? children(call.transcript || [], undefined)
      : translated ? children(currentResult.transcript, language) : null}
    {showingTranslation ? <div className="call-transcript-translation-status" aria-live="polite">
      {currentResult?.error ? <><p role="alert">{currentResult.error}{translated ? ' Completed turns remain above.' : ' Showing the original transcript.'}</p><Button size="sm" variant="secondary" onClick={() => setRetry(value => value + 1)}>Try again</Button></>
        : !currentResult?.complete ? <div className="call-transcript-translation-progress">
          <span aria-hidden="true"><LoadingIndicator /></span>
          <span>{translated ? 'Translating remaining conversation…' : 'Translating opening turns…'}</span>
        </div> : null}
    </div> : null}
    {showingTranslation && currentResult?.error && !translated ? children(call.transcript || [], undefined) : null}
  </>;
}
